import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { empresaService, EmpresaResumen } from '../services/empresaService';
import { useTenantStore } from '../store/tenantStore';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import { useAuth } from '../hooks/useAuth';

const SelectEmpresa: React.FC = () => {
  const navigate = useNavigate();
  const { setTenant } = useTenantStore();
  const { logout, isLogoutLoading, isAuthenticated } = useAuth();

  const { data: empresas, isLoading, isError } = useQuery<EmpresaResumen[]>({
    queryKey: ['misEmpresas'],
    queryFn: empresaService.getMisEmpresas,
    retry: (failureCount, error: any) => {
      // Si es 401 no reintentar; el interceptor ya redirige a login
      if (error?.response?.status === 401) return false;
      return failureCount < 2;
    },
  });

  React.useEffect(() => {
    if (empresas && empresas.length === 1) {
      setTenant(empresas[0].id, empresas[0].nombre);
      navigate('/app', { replace: true });
    }
  }, [empresas, setTenant, navigate]);

  if (!isAuthenticated) {
    // El interceptor ya redirige a /login, mostramos loader breve
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Redirigiendo a inicio de sesión…</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Cargando empresas...</div>
      </div>
    );
  }

  if (isError || !empresas) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-red-500">Error al cargar las empresas</div>
      </div>
    );
  }

  if (empresas.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <Logo size="lg" />
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="max-w-lg w-full bg-white shadow-lg rounded-xl p-8 text-center">
            <div className="text-gray-700 mb-6">
              No tienes empresas asignadas. Contacta a un administrador.
            </div>
            <button
              onClick={() => logout()}
              disabled={isLogoutLoading}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 disabled:opacity-60"
            >
              {isLogoutLoading ? 'Cerrando sesión…' : 'Cerrar sesión'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Logo size="lg" />
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-lg w-full bg-white shadow-lg rounded-xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Selecciona una empresa</h1>
            <p className="text-gray-600">Elige la empresa con la que quieres trabajar</p>
          </div>
          
          <ul className="space-y-4">
            {empresas.map((e) => (
              <li key={e.id}>
                <button
                  onClick={() => { setTenant(e.id, e.nombre); navigate('/app', { replace: true }); }}
                  className="w-full text-left p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-indigo-300 transition-all duration-200"
                >
                  <div className="font-semibold text-gray-900">{e.nombre}</div>
                  <div className="text-sm text-gray-500">{e.slug}</div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SelectEmpresa;
